import React from 'react';

const ClosingReport = ({ session, movements, onClose }) => {
    if (!session) return null;

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('es-CO');
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    const print = () => {
        window.print();
    };

    // Filter movements
    const incomeMovements = movements.filter(m => ['extra_income', 'sale_income'].includes(m.type));
    const expenseMovements = movements.filter(m => ['withdrawal', 'supplier_payment', 'operational_expense', 'employee_advance', 'bank_deposit'].includes(m.type));

    const totalIncomeMovements = incomeMovements.reduce((sum, m) => sum + parseFloat(m.amount), 0);
    const totalExpenseMovements = expenseMovements.reduce((sum, m) => sum + parseFloat(m.amount), 0);

    return (
        <div className="fixed inset-0 bg-white z-[100] overflow-auto">
            <div className="max-w-3xl mx-auto p-8 print:p-0">
                {/* Print Controls */}
                <div className="flex justify-between items-center mb-8 print:hidden">
                    <button
                        onClick={onClose}
                        className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                    >
                        &larr; Volver
                    </button>
                    <button
                        onClick={print}
                        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 font-bold"
                    >
                        🖨️ Imprimir Reporte
                    </button>
                </div>

                {/* Report Content */}
                <div className="border border-gray-300 p-8 print:border-0 print:p-0">
                    {/* Header */}
                    <div className="text-center border-b pb-6 mb-6">
                        <h1 className="text-2xl font-bold uppercase tracking-wide mb-2">Reporte de Cierre de Caja</h1>
                        <h2 className="text-xl font-medium text-gray-700">{session.box_name}</h2>
                        <div className="text-sm text-gray-500 mt-2">ID Sesión: #{session.id}</div>
                    </div>

                    {/* Meta Info */}
                    <div className="grid grid-cols-2 gap-8 mb-8 text-sm">
                        <div>
                            <div className="flex justify-between py-1 border-b border-gray-100">
                                <span className="font-medium text-gray-600">Apertura:</span>
                                <span>{formatDate(session.opened_at)}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-gray-100">
                                <span className="font-medium text-gray-600">Responsable (Apertura):</span>
                                <span>{session.opened_by_name}</span>
                            </div>
                        </div>
                        <div>
                            <div className="flex justify-between py-1 border-b border-gray-100">
                                <span className="font-medium text-gray-600">Cierre:</span>
                                <span>{formatDate(session.closed_at)}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-gray-100">
                                <span className="font-medium text-gray-600">Responsable (Cierre):</span>
                                <span>{session.closed_by_name}</span>
                            </div>
                        </div>
                    </div>

                    {/* Financial Summary */}
                    <div className="mb-8">
                        <h3 className="font-bold text-gray-800 border-b-2 border-gray-800 pb-2 mb-4">Resumen Financiero</h3>
                        <table className="w-full text-sm">
                            <tbody>
                                <tr className="border-b border-gray-100">
                                    <td className="py-2 font-medium">Base Inicial</td>
                                    <td className="py-2 text-right">{formatCurrency(session.opening_balance)}</td>
                                </tr>
                                <tr className="border-b border-gray-100">
                                    <td className="py-2 font-medium text-green-700">Total Ingresos Recaudados (Rutas + Caja)</td>
                                    <td className="py-2 text-right text-green-700">
                                        + {formatCurrency((parseFloat(session.closing_balance_expected) - parseFloat(session.opening_balance) + totalExpenseMovements))}
                                    </td>
                                </tr>
                                <tr className="border-b border-gray-100">
                                    <td className="py-2 font-medium text-red-700">Total Egresos Registrados</td>
                                    <td className="py-2 text-right text-red-700">- {formatCurrency(totalExpenseMovements)}</td>
                                </tr>
                                <tr className="border-t-2 border-gray-200 font-bold bg-gray-50">
                                    <td className="py-3 pl-2">Saldo Esperado en Caja</td>
                                    <td className="py-3 text-right pr-2">{formatCurrency(session.closing_balance_expected)}</td>
                                </tr>
                                <tr className="border-b border-gray-200">
                                    <td className="py-3 pl-2 font-medium">Saldo Efectivo Declarado (Conteo)</td>
                                    <td className="py-3 text-right pr-2 text-blue-800 font-bold">{formatCurrency(session.closing_balance_declared)}</td>
                                </tr>
                                <tr className={`font-bold text-lg ${parseFloat(session.difference) === 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    <td className="py-4 pl-2">Diferencia / Descuadre</td>
                                    <td className="py-4 text-right pr-2">{formatCurrency(session.difference)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Denomination Breakdown */}
                    {session.closing_breakdown && (
                        <div className="mb-8 break-inside-avoid">
                            <h3 className="font-bold text-gray-800 border-b pb-2 mb-4">Desglose de Efectivo</h3>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                {Object.entries(JSON.parse(session.closing_breakdown)).map(([denom, qty]) => (
                                    qty > 0 && (
                                        <div key={denom} className="flex justify-between border-b border-gray-100 py-1">
                                            <span className="text-gray-600 capitalize">{denom.replace('k', '.000').replace('coins', 'Monedas')}</span>
                                            <span className="font-mono font-medium">{qty}</span>
                                        </div>
                                    )
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Detailed Movements List (Split) */}
                    <div className="grid grid-cols-2 gap-8 mb-8 break-inside-avoid">
                        {/* Incomes */}
                        <div>
                            <h3 className="font-bold text-green-800 border-b border-green-200 pb-2 mb-4">Detalle de Ingresos</h3>

                            {/* Ruta Sales */}
                            {movements.filter(m => m.type === 'sale_income' && m.source === 'ruta').length > 0 && (
                                <div className="mb-6">
                                    <div className="text-xs font-bold text-green-700 uppercase mb-2 ml-1 bg-green-50 inline-block px-1 rounded">Ventas Ruta</div>
                                    <table className="w-full text-[10px] text-left mb-2 border-collapse">
                                        <thead className="bg-green-50 text-green-800 border-b border-green-100">
                                            <tr>
                                                <th className="py-1 px-1">#</th>
                                                <th className="py-1 px-1">Pedido / INT</th>
                                                <th className="py-1 px-1">Cliente</th>
                                                <th className="py-1 px-1">Fechas</th>
                                                <th className="py-1 px-1 text-right">Valores</th>
                                                <th className="py-1 px-1">Métodos</th>
                                                <th className="py-1 px-1 text-center">Estado RC</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-green-50">
                                            {movements.filter(m => m.type === 'sale_income' && m.source === 'ruta').map((m, idx) => {
                                                // Parse RC info
                                                let rcInfo = null;
                                                if (m.siigo_payment_info) {
                                                    try {
                                                        const parsed = JSON.parse(m.siigo_payment_info);
                                                        if (Array.isArray(parsed) && parsed.length > 0) rcInfo = parsed[0];
                                                    } catch (e) { }
                                                }
                                                // Get INT order info if available
                                                const intOrder = m.int_order_number; // Available from backend query

                                                return (
                                                    <tr key={m.id}>
                                                        <td className="py-1 px-1 text-gray-500 w-6">{idx + 1}</td>
                                                        <td className="py-1 px-1 align-top">
                                                            <div className="font-bold">{m.order_number || m.concept}</div>
                                                            {intOrder && <div className="text-purple-600 text-[9px] mt-0.5">🔗 {intOrder}</div>}
                                                        </td>
                                                        <td className="py-1 px-1 align-top w-24 truncate">
                                                            {m.beneficiary || m.customer_name}
                                                        </td>
                                                        <td className="py-1 px-1 align-top text-[9px] text-gray-500 w-24">
                                                            <div>Fac: {m.invoice_date ? new Date(m.invoice_date).toLocaleDateString('es-CO') : '-'}</div>
                                                            <div>Rec: {m.created_at ? new Date(m.created_at).toLocaleString('es-CO', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</div>
                                                        </td>
                                                        <td className="py-1 px-1 align-top text-right w-20">
                                                            <div className="font-bold text-gray-800">{formatCurrency(m.amount)}</div>
                                                            <div className="text-[9px] text-gray-500">Fac: {formatCurrency(m.invoice_total)}</div>
                                                            {m.int_total_amount > 0 && <div className="text-[9px] text-purple-600">Int: {formatCurrency(m.int_total_amount)}</div>}
                                                        </td>
                                                        <td className="py-1 px-1 align-top text-[9px] w-20">
                                                            <div>💳 {m.payment_method}</div>
                                                            <div className="text-gray-500">📦 {m.delivery_method}</div>
                                                        </td>
                                                        <td className="py-1 px-1 align-top text-center w-24">
                                                            {rcInfo ? (
                                                                <span className="inline-block px-1 py-0.5 bg-green-100 text-green-800 rounded text-[9px] whitespace-nowrap">
                                                                    ✅ {rcInfo.name || rcInfo.number}
                                                                </span>
                                                            ) : (
                                                                <span className="inline-block px-1 py-0.5 bg-orange-100 text-orange-800 rounded text-[9px]">
                                                                    ⏳ Pendiente
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot className="bg-green-50/50 font-bold border-t border-green-100">
                                            <tr>
                                                <td colSpan="4" className="py-1 px-2 text-right">Total Ruta:</td>
                                                <td className="py-1 px-1 text-right text-green-700">
                                                    {formatCurrency(movements.filter(m => m.type === 'sale_income' && m.source === 'ruta').reduce((sum, m) => sum + parseFloat(m.amount), 0))}
                                                </td>
                                                <td colSpan="2"></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}

                            {/* Bodega Sales */}
                            {movements.filter(m => m.type === 'sale_income' && m.source === 'bodega').length > 0 && (
                                <div className="mb-6">
                                    <div className="text-xs font-bold text-green-700 uppercase mb-2 ml-1 bg-green-50 inline-block px-1 rounded">Ventas Bodega</div>
                                    <table className="w-full text-[10px] text-left mb-2 border-collapse">
                                        <thead className="bg-green-50 text-green-800 border-b border-green-100">
                                            <tr>
                                                <th className="py-1 px-1">#</th>
                                                <th className="py-1 px-1">Pedido / INT</th>
                                                <th className="py-1 px-1">Cliente</th>
                                                <th className="py-1 px-1">Fechas</th>
                                                <th className="py-1 px-1 text-right">Valores</th>
                                                <th className="py-1 px-1">Métodos</th>
                                                <th className="py-1 px-1">Entregado Por</th>
                                                <th className="py-1 px-1 text-center">Estado RC</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-green-50">
                                            {movements.filter(m => m.type === 'sale_income' && m.source === 'bodega').map((m, idx) => {
                                                // Parse RC info
                                                let rcInfo = null;
                                                if (m.siigo_payment_info) {
                                                    try {
                                                        const parsed = JSON.parse(m.siigo_payment_info);
                                                        if (Array.isArray(parsed) && parsed.length > 0) rcInfo = parsed[0];
                                                    } catch (e) { }
                                                }
                                                const intOrder = m.int_order_number;

                                                return (
                                                    <tr key={m.id}>
                                                        <td className="py-1 px-1 text-gray-500 w-6">{idx + 1}</td>
                                                        <td className="py-1 px-1 align-top">
                                                            <div className="font-bold">{m.order_number || m.concept}</div>
                                                            {intOrder && <div className="text-purple-600 text-[9px] mt-0.5">🔗 {intOrder}</div>}
                                                        </td>
                                                        <td className="py-1 px-1 align-top w-24 truncate">
                                                            {m.beneficiary || m.customer_name}
                                                        </td>
                                                        <td className="py-1 px-1 align-top text-[9px] text-gray-500 w-24">
                                                            <div>Fac: {m.invoice_date ? new Date(m.invoice_date).toLocaleDateString('es-CO') : '-'}</div>
                                                            <div>Rec: {m.created_at ? new Date(m.created_at).toLocaleString('es-CO', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</div>
                                                        </td>
                                                        <td className="py-1 px-1 align-top text-right w-20">
                                                            <div className="font-bold text-gray-800">{formatCurrency(m.amount)}</div>
                                                            <div className="text-[9px] text-gray-500">Fac: {formatCurrency(m.invoice_total)}</div>
                                                            {m.int_total_amount > 0 && <div className="text-[9px] text-purple-600">Int: {formatCurrency(m.int_total_amount)}</div>}
                                                        </td>
                                                        <td className="py-1 px-1 align-top text-[9px] w-20">
                                                            <div>💳 {m.payment_method}</div>
                                                            <div className="text-gray-500">📦 {m.delivery_method}</div>
                                                        </td>
                                                        <td className="py-1 px-1 align-top w-20">
                                                            {m.delivered_by || 'Bodega'}
                                                        </td>
                                                        <td className="py-1 px-1 align-top text-center w-24">
                                                            {rcInfo ? (
                                                                <span className="inline-block px-1 py-0.5 bg-green-100 text-green-800 rounded text-[9px] whitespace-nowrap">
                                                                    ✅ {rcInfo.name || rcInfo.number}
                                                                </span>
                                                            ) : (
                                                                <span className="inline-block px-1 py-0.5 bg-orange-100 text-orange-800 rounded text-[9px]">
                                                                    ⏳ Pendiente
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot className="bg-green-50/50 font-bold border-t border-green-100">
                                            <tr>
                                                <td colSpan="4" className="py-1 px-2 text-right">Total Bodega:</td>
                                                <td className="py-1 px-1 text-right text-green-700">
                                                    {formatCurrency(movements.filter(m => m.type === 'sale_income' && m.source === 'bodega').reduce((sum, m) => sum + parseFloat(m.amount), 0))}
                                                </td>
                                                <td colSpan="3"></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}

                            {/* Other Income (Simple Table) */}
                            {movements.filter(m => m.type === 'extra_income').length > 0 && (
                                <div className="mb-4">
                                    <div className="text-[10px] font-bold text-green-700 uppercase mb-1 ml-1 bg-green-50 inline-block px-1 rounded">Otros Ingresos</div>
                                    <table className="w-full text-xs text-left">
                                        <tbody className="divide-y divide-green-50">
                                            {movements.filter(m => m.type === 'extra_income').map(m => (
                                                <tr key={m.id}>
                                                    <td className="py-1 px-1 w-16">{new Date(m.created_at).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}</td>
                                                    <td className="py-1 px-1">
                                                        {m.concept}
                                                        {m.beneficiary && <span className="text-gray-500"> ({m.beneficiary})</span>}
                                                    </td>
                                                    <td className="py-1 px-1 text-right font-mono text-green-700 w-20">{formatCurrency(m.amount)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {movements.filter(m => ['extra_income', 'sale_income'].includes(m.type)).length === 0 && (
                                <div className="py-2 text-center text-gray-400 italic text-xs">Sin ingresos registrados</div>
                            )}
                        </div>

                        {/* Expenses (No Changes) */}
                        <div>
                            <h3 className="font-bold text-red-800 border-b border-red-200 pb-2 mb-4">Detalle de Egresos</h3>
                            <table className="w-full text-xs text-left">
                                <thead className="bg-red-50 uppercase tracking-wider font-bold text-red-800">
                                    <tr>
                                        <th className="py-2 px-1">Hora</th>
                                        <th className="py-2 px-1">Concepto</th>
                                        <th className="py-2 px-1 text-right">Monto</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-red-50">
                                    {movements.filter(m => !['extra_income', 'sale_income'].includes(m.type)).map(m => (
                                        <tr key={m.id}>
                                            <td className="py-1 px-1">{new Date(m.created_at).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}</td>
                                            <td className="py-1 px-1">
                                                {m.concept}
                                                <div className="text-[10px] text-gray-500 uppercase">{m.type.replace('_', ' ')}</div>
                                            </td>
                                            <td className="py-1 px-1 text-right font-mono text-red-700">{formatCurrency(m.amount)}</td>
                                        </tr>
                                    ))}
                                    {movements.filter(m => !['extra_income', 'sale_income'].includes(m.type)).length === 0 && (
                                        <tr><td colSpan="3" className="py-2 text-center text-gray-400 italic">Sin egresos registrados</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Signatures Area */}
                    <div className="mt-16 grid grid-cols-2 gap-16 break-inside-avoid">
                        <div className="text-center">
                            <div className="border-t border-gray-400 pt-2">
                                <span className="block font-bold text-gray-800">{session.closed_by_name}</span>
                                <span className="block text-xs text-gray-500 uppercase">Responsable (Entrega)</span>
                            </div>
                        </div>
                        <div className="text-center">
                            <div className="border-t border-gray-400 pt-2">
                                <span className="block font-bold text-gray-800">Recibido / Auditoría</span>
                                <span className="block text-xs text-gray-500 uppercase">Firma de Conformidad</span>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="text-center text-xs text-gray-400 mt-12 print:mt-8">
                        Generado el {new Date().toLocaleString('es-CO')} • Sistema de Gestión de Pedidos
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClosingReport;
